import { Logger } from "./logger";
import {
  HTTP_RETRY_DELAY_MS,
  HTTP_MAX_RETRIES,
  HTTP_MAX_RETRY_AFTER_MS,
  HTTP_REQUEST_TIMEOUT_MS,
  HTTP_CREDENTIALS_MODE,
} from "./config";
import type { APIResponse, APIErrorResponse, APIResult } from "../types";

interface HttpClientConfig {
  baseURL: string;
  apiKey: string;
  apiSecret?: string;
}

export class HttpClient {
  private baseURL: string;
  private apiKey: string;
  private apiSecret: string | undefined;

  constructor(config: HttpClientConfig) {
    this.baseURL = config.baseURL.replace(/\/+$/, "");
    this.apiKey = config.apiKey;
    this.apiSecret = config.apiSecret;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    let url = `${this.baseURL}${path}`;
    if (params) {
      const query = new URLSearchParams(params).toString();
      url += `?${query}`;
    }
    return this.request<T>(url, { method: "GET" });
  }

  async post<T>(
    path: string,
    body: unknown,
    options?: { useSecret?: boolean; params?: Record<string, string> },
  ): Promise<T> {
    let url = `${this.baseURL}${path}`;
    if (options?.params) {
      const query = new URLSearchParams(options.params).toString();
      url += `?${query}`;
    }
    return this.request<T>(url, {
      method: "POST",
      body: JSON.stringify(body),
      useSecret: options?.useSecret,
    });
  }

  private async request<T>(
    url: string,
    options: { method: string; body?: string; useSecret?: boolean },
    retryCount = 0,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-API-Key": this.apiKey,
    };

    if (options.body) {
      headers["Content-Type"] = "application/json";
    }

    if (options.useSecret && this.apiSecret) {
      headers["X-API-Secret"] = this.apiSecret;
    }

    Logger.log(`${options.method} ${url}`, options.body ? JSON.parse(options.body) : undefined);

    let response: Response;

    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), HTTP_REQUEST_TIMEOUT_MS)
      : null;

    try {
      response = await fetch(url, {
        method: options.method,
        headers,
        body: options.body,
        credentials: HTTP_CREDENTIALS_MODE,
        signal: controller?.signal,
      });
    } catch (err) {
      const isTimeout = controller?.signal.aborted === true;
      const label = isTimeout ? "Request timeout" : "Network error";
      Logger.error(`${label}: ${options.method} ${url}`, err);

      if (retryCount < HTTP_MAX_RETRIES) {
        Logger.log(`Retrying in ${HTTP_RETRY_DELAY_MS}ms... (attempt ${retryCount + 2})`);
        await this.delay(HTTP_RETRY_DELAY_MS);
        return this.request<T>(url, options, retryCount + 1);
      }

      throw new HttpError(label, 0, null);
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId);
    }

    if (response.status === 429) {
      if (retryCount < HTTP_MAX_RETRIES) {
        const retryAfter = this.getRetryAfter(response);
        Logger.warn(`Rate limited. Retrying in ${retryAfter}ms...`);
        await this.delay(retryAfter);
        return this.request<T>(url, options, retryCount + 1);
      }
      Logger.error("Rate limited, max retries reached");
      throw new HttpError("Rate limit exceeded", 429, null);
    }

    if (response.status >= 500 && retryCount < HTTP_MAX_RETRIES) {
      Logger.warn(`Server error ${response.status}. Retrying in ${HTTP_RETRY_DELAY_MS}ms...`);
      await this.delay(HTTP_RETRY_DELAY_MS);
      return this.request<T>(url, options, retryCount + 1);
    }

    let result: APIResult<T>;
    try {
      result = await response.json();
    } catch {
      Logger.error(`Invalid JSON response: ${options.method} ${url}`);
      throw new HttpError("Invalid JSON response", response.status, null);
    }

    Logger.log(`Response ${response.status}`, result);

    const remaining = response.headers.get("X-RateLimit-Remaining");
    if (remaining !== null) {
      Logger.log(`Rate limit remaining: ${remaining}`);
    }

    if (!result.success) {
      const errorResult = result as APIErrorResponse;
      const errorObj = errorResult.error;
      const errorCode = errorObj?.code ?? "UNKNOWN";
      const errorMessage = errorObj?.message ?? `Server error (HTTP ${response.status})`;
      Logger.error(`API error: ${errorCode} — ${errorMessage}`);
      throw new HttpError(errorMessage, response.status, errorObj ?? null);
    }

    return (result as APIResponse<T>).data;
  }

  private getRetryAfter(response: Response): number {
    const retryAfter = response.headers.get("Retry-After");
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      if (!isNaN(seconds)) return Math.min(seconds * 1000, HTTP_MAX_RETRY_AFTER_MS);
    }
    const resetAt = response.headers.get("X-RateLimit-Reset");
    if (resetAt) {
      const resetTime = parseInt(resetAt, 10) * 1000;
      const waitMs = resetTime - Date.now();
      if (waitMs > 0) return Math.min(waitMs, HTTP_MAX_RETRY_AFTER_MS);
    }
    return HTTP_RETRY_DELAY_MS * 2;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export class HttpError extends Error {
  status: number;
  apiError: APIErrorResponse["error"] | null;

  constructor(message: string, status: number, apiError: APIErrorResponse["error"] | null) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.apiError = apiError;
  }
}
